import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import moment from 'moment';
import { trackById } from '../../../../utils/track-by';
import icSettings from '@iconify/icons-ic/twotone-settings';
import icChevronRight from '@iconify/icons-ic/twotone-chevron-right';
import icNotificationsOff from '@iconify/icons-ic/twotone-notifications-off';
import icClearAll from '@iconify/icons-ic/twotone-clear-all';
import { NotificationService, NotificationItem } from 'src/app/helpers/services/notification.service';
import { firstValueFrom } from 'rxjs';

interface DropdownNotification {
  id: number;
  icon: string;
  colorClass: string;
  label: string;
  description: string;
  datetime: moment.Moment;
  read: boolean;
}

const TYPE_ICON_MAP: Record<string, { icon: string; colorClass: string }> = {
  mention: { icon: 'ic:twotone-alternate-email', colorClass: 'text-primary' },
  task_assignment: { icon: 'ic:twotone-assignment-ind', colorClass: 'text-orange' },
  approval: { icon: 'ic:twotone-check-circle', colorClass: 'text-green' },
};
const DEFAULT_ICON = { icon: 'ic:twotone-notifications', colorClass: 'text-primary' };

@Component({
  selector: 'vex-toolbar-notifications-dropdown',
  templateUrl: './toolbar-notifications-dropdown.component.html',
  styleUrls: ['./toolbar-notifications-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ToolbarNotificationsDropdownComponent implements OnInit {
  notifications: DropdownNotification[] = [];
  loading = true;

  icSettings = icSettings;
  icChevronRight = icChevronRight;
  icClearAll = icClearAll;
  icNotificationsOff = icNotificationsOff;
  trackById = trackById;

  constructor(
    private readonly notificationService: NotificationService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.cd.markForCheck();
    try {
      const res = await firstValueFrom(this.notificationService.getNotifications());
      this.notifications = res.data.map(n => this.toDisplay(n));
    } catch {
      this.notifications = [];
    } finally {
      this.loading = false;
      this.cd.markForCheck();
    }
  }

  async markRead(notification: DropdownNotification): Promise<void> {
    if (notification.read) return;
    try {
      await firstValueFrom(this.notificationService.markAsRead(notification.id));
      notification.read = true;
      this.notificationService.refreshCount();
      this.cd.markForCheck();
    } catch {}
  }

  async markAllRead(): Promise<void> {
    try {
      await firstValueFrom(this.notificationService.markAllAsRead());
      this.notifications.forEach(n => (n.read = true));
      this.notificationService.refreshCount();
      this.cd.markForCheck();
    } catch {}
  }

  get unreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  private toDisplay(item: NotificationItem): DropdownNotification {
    const { icon, colorClass } = TYPE_ICON_MAP[item.type] ?? DEFAULT_ICON;
    return {
      id: item.id,
      icon,
      colorClass,
      label: item.title ?? item.type,
      description: item.message ?? '',
      datetime: item.created_date ? moment(item.created_date) : moment(),
      read: !item.unread,
    };
  }
}
